import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Card, Spinner } from './ui.js';
import { api, type JoinCode, type LiveSession, type MeetingCredential } from '../lib/api.js';
import { formatNumber, formatRelative } from '../lib/format.js';
import { useAsync } from '../lib/hooks.js';

/**
 * What a teacher opens the console to find out.
 *
 * This page used to be a billing report — subscription, usage meters, plan
 * features — on a product whose billing is switched off. A teacher signing in
 * an hour before class wants one answer, "is my next class ready", and had to
 * navigate away to get it.
 *
 * Every number here comes from an endpoint that already existed, so this is a
 * rearrangement rather than a new read path, and each tile links to the page
 * that can act on it: a count nobody can do anything about is decoration.
 */
export function TeachingSummary({
  orgId,
  canManage,
}: {
  orgId: string;
  canManage: boolean;
}): ReactNode {
  const upcoming = useAsync(
    () =>
      api.get<{ data: LiveSession[] }>(
        `/api/organizations/${orgId}/live-sessions?state=upcoming&limit=100`,
      ),
    [orgId],
  );
  const students = useAsync(
    () => api.get<{ data: { id: string }[] }>(`/api/organizations/${orgId}/members`),
    [orgId],
  );
  /*
   * Codes and credentials are behind `manage`, so a plain member asking for them
   * would get a 403 and an error banner on their own home page.
   */
  const codes = useAsync(
    () =>
      canManage
        ? api.get<{ data: JoinCode[] }>(`/api/organizations/${orgId}/join-codes`)
        : Promise.resolve({ data: [] as JoinCode[] }),
    [orgId, canManage],
  );
  const credentials = useAsync(
    () =>
      canManage
        ? api.get<{ data: MeetingCredential[] }>(`/api/organizations/${orgId}/meeting-credentials`)
        : Promise.resolve({ data: [] as MeetingCredential[] }),
    [orgId, canManage],
  );

  const classes = upcoming.data?.data ?? [];
  /*
   * The list is already ascending by start time for this tab — part of the API
   * contract — so the next class is the first row. Not re-sorted here, because
   * two places deciding the order is how they come to disagree.
   */
  const next = classes[0] ?? null;

  const codeRows = codes.data?.data ?? [];
  const claimed = codeRows.filter((row) => row.redeemedAt !== null).length;
  const unspent = codeRows.filter((row) => row.status === 'active').length;

  const zoomReady = (credentials.data?.data ?? []).some(
    (row) => row.active && row.provider === 'zoom',
  );

  return (
    <div className="stack">
      <Card
        title="Next class"
        actions={
          <Link
            to={`/orgs/${orgId}/live-sessions`}
            /*
             * Primary weight for a publisher, secondary for everyone else.
             * Scheduling is the one thing a teacher comes here to do, and it was
             * drawn as quietly as "Refresh".
             */
            className={`button ${canManage ? 'button-primary' : 'button-secondary'}`}
          >
            {canManage ? 'Schedule a class' : 'All classes'}
          </Link>
        }
      >
        {upcoming.loading ? (
          <Spinner />
        ) : next ? (
          <div className="stack stack-sm">
            <div className="row">
              <strong>{next.title}</strong>
              {next.joinable && <span className="badge badge-success">Doors open</span>}
              {next.state === 'live' && <span className="badge badge-success">Live now</span>}
            </div>
            <p className="small muted">
              {formatRelative(next.startsAt)} · {next.durationMinutes} min ·{' '}
              {next.kind === 'zoom' ? 'Zoom' : 'YouTube Live'}
            </p>
            {next.hostName && <p className="small faint">Hosted by {next.hostName}</p>}
          </div>
        ) : (
          <p className="muted small">
            Nothing scheduled.{' '}
            {canManage && (
              <Link to={`/orgs/${orgId}/live-sessions`}>Schedule your first class</Link>
            )}
          </p>
        )}
      </Card>

      <div className="grid">
        <Stat
          label="Classes to come"
          value={formatNumber(classes.length)}
          to={`/orgs/${orgId}/live-sessions`}
          loading={upcoming.loading}
        />
        <Stat
          label="People in this institute"
          value={formatNumber(students.data?.data.length ?? 0)}
          to={`/orgs/${orgId}/members`}
          loading={students.loading}
        />
        {canManage && (
          <Stat
            label="Codes claimed"
            value={`${formatNumber(claimed)} of ${formatNumber(codeRows.length)}`}
            hint={unspent > 0 ? `${formatNumber(unspent)} still unused` : undefined}
            to={`/orgs/${orgId}/members`}
            loading={codes.loading}
          />
        )}
      </div>

      {/*
        Shown only when it is actionable and only to somebody who can act. A
        publisher without Zoom credentials can schedule a class that no student
        can ever join, and the first they would learn of it is a student saying
        so — this is the one warning worth putting on the front page.
      */}
      {canManage && !credentials.loading && !zoomReady && (
        <div className="banner banner-warning">
          No Zoom account is connected yet, so students cannot join a Zoom class.{' '}
          <Link to={`/orgs/${orgId}/live-sessions`}>Connect Zoom</Link>
        </div>
      )}
    </div>
  );
}

/** One number, with the page that can do something about it behind it. */
function Stat({
  label,
  value,
  hint,
  to,
  loading,
}: {
  label: string;
  value: string;
  hint?: string | undefined;
  to: string;
  loading: boolean;
}): ReactNode {
  return (
    <Link to={to} className="stat">
      <span className="stat-label">{label}</span>
      <span className="stat-value">{loading ? <Spinner /> : value}</span>
      {hint && <span className="small faint">{hint}</span>}
    </Link>
  );
}
